/**
 * Données du planning v2 (docs/ux-v2/SPEC.md § 5.1) : `GET mindbody/v1/class/planning`.
 *
 * Une marque à la fois (retours du 07/10) : la requête porte `siteId`, le site de la marque affichée (filtre Marque ;
 * à l'ouverture, le site visité) ; le serveur le refuse s'il n'est pas de la ville du site courant. Une autre marque
 * est chargée au clic sur son étiquette (nouvelle requête, mise en cache par semaine et par site) ; Studios, Coachs et
 * Cours s'appliquent dans le navigateur, sans requête. `meta.siteIds` donne toujours les sites de la ville (étiquettes
 * Marque), `meta.loadedSiteIds` ceux dont les cours sont dans la réponse. Le serveur garde chaque semaine de chaque
 * site 5 min, pour tous les visiteurs (PlanningService).
 * Les descriptions des cours arrivent une seule fois, dans `descriptions` (« SiteId:ClassDescription.Id ») :
 * normalizePlanningResponse les remet dans chaque cours (lues par la fiche cours déconnectée).
 *
 * Repli : si la route répond 404 (serveur sans la route, ou aperçu administrateur `?ux=v2` — les requêtes REST
 * y voient un visiteur anonyme, cf. apps/server/UxV2.php), le planning du site seul est lu par la route v1
 * `GET class`, avec les mêmes paramètres que screens/Classes (une seule requête de plus, seulement dans ce cas).
 */
import { useQuery } from '@tanstack/react-query';
import type { MindbodyClass } from 'types/class';
import { HTTP } from 'utils/http';

/** Cours projeté par `class/planning` (SPEC § 5.1). */
export interface PlanningClass {
  Id: number;
  ClassScheduleId?: number;
  StartDateTime: string;
  EndDateTime: string;
  Active: boolean;
  IsCanceled: boolean;
  IsAvailable?: boolean;
  BookingStatus?: string | null;
  MaxCapacity: number;
  WebCapacity?: number;
  TotalBooked: number;
  WebBooked?: number;
  TotalBookedWaitlist: number;
  WaitListSize: number;
  IsWaitlistAvailable?: boolean;
  ThemeName?: string | null;
  IsCourse?: boolean;
  ClassDescription: {
    Id?: number;
    Name: string;
    Description?: string | null;
    Program: { Id: number; ScheduleType: string };
    SessionType?: { Id?: number; Name?: string; NumDeducted?: number };
  };
  Location: {
    Id: number;
    SiteID: number;
    Name: string;
    Address?: string | null;
    Address2?: string | null;
    City?: string | null;
    PostalCode?: string | null;
  };
  Staff: { Id: number; FirstName: string };
  /** Site Mindbody du cours (copie de Location.SiteID). */
  SiteId: number;
}

export interface PlanningMeta {
  /** Sites de la ville (une étiquette Marque par site en vue multi-marques). */
  siteIds: number[];
  /** Sites dont les cours sont dans la réponse (le site demandé ; tous ceux de `siteIds` sans `siteId`). */
  loadedSiteIds: number[];
  multiBrand: boolean;
  failedSiteIds: number[];
  /** Vrai quand la réponse vient du repli v1 (`GET class`, site seul). */
  fallback?: boolean;
}

export interface PlanningResponse {
  classes: PlanningClass[];
  meta: PlanningMeta;
}

export interface PlanningParams {
  week: number;
  year: number;
  /** Mode coach (shortcode `[mindbody coach]`) : restreint la requête à ce coach, comme la v1. */
  coachId?: number | null;
  /** Site Mindbody dont on charge les cours (marque affichée) ; absent : le choix du serveur. */
  siteId?: string | null;
  /** Site courant : repli et normalisation. */
  currentSiteId: string;
}

/** Query string de `class/planning` : `week=41&year=2026[&siteId=…][&coachIds[]=…]`. */
export function planningQueryString({
  week,
  year,
  coachId,
  siteId,
}: Pick<PlanningParams, 'week' | 'year' | 'coachId' | 'siteId'>): string {
  const parts = [`week=${encodeURIComponent(String(week))}`, `year=${encodeURIComponent(String(year))}`];
  if (siteId) parts.push(`siteId=${encodeURIComponent(siteId)}`);
  if (coachId) parts.push(`${encodeURIComponent('coachIds[]')}=${encodeURIComponent(String(coachId))}`);
  return parts.join('&');
}

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};

/** Garantit `SiteId` (nombre) sur chaque cours, quelle que soit la source. */
export function normalizeClasses(classes: unknown): PlanningClass[] {
  if (!Array.isArray(classes)) return [];
  return classes
    .filter((c): c is PlanningClass => !!c && typeof c === 'object' && 'StartDateTime' in c && 'Location' in c)
    .map((c) => ({ ...c, SiteId: toNumber(c.SiteId ?? c.Location?.SiteID) }));
}

/** Remet la description de chaque cours, envoyée une seule fois par le serveur (`descriptions`). */
export function withDescriptions(classes: PlanningClass[], descriptions: unknown): PlanningClass[] {
  if (!descriptions || typeof descriptions !== 'object' || Array.isArray(descriptions)) return classes;
  const byKey = descriptions as Record<string, unknown>;
  return classes.map((c) => {
    if (c.ClassDescription?.Description) return c;
    const html = byKey[`${c.SiteId}:${c.ClassDescription?.Id}`];
    return typeof html === 'string' && html !== '' ? { ...c, ClassDescription: { ...c.ClassDescription, Description: html } } : c;
  });
}

/** Réponse de `class/planning` (forme SPEC § 5.1), tolérante aux champs manquants. */
export function normalizePlanningResponse(raw: unknown, currentSiteId: string): PlanningResponse {
  const body = (raw && typeof raw === 'object' ? raw : {}) as Partial<PlanningResponse> & { classes?: unknown; descriptions?: unknown };
  const classes = withDescriptions(normalizeClasses(body.classes), body.descriptions);
  const meta = (body.meta ?? {}) as Partial<PlanningMeta>;
  const classSites = [...new Set(classes.map((c) => c.SiteId))].filter(Boolean);
  const siteIds = Array.isArray(meta.siteIds) && meta.siteIds.length ? meta.siteIds.map(toNumber).filter(Boolean) : classSites;
  const loadedSiteIds =
    Array.isArray(meta.loadedSiteIds) && meta.loadedSiteIds.length ? meta.loadedSiteIds.map(toNumber).filter(Boolean) : null;
  const sites = siteIds.length ? siteIds : [toNumber(currentSiteId)];
  return {
    classes,
    meta: {
      siteIds: sites,
      // Serveur d'avant le paramètre `siteId` : tous les sites de la ville sont dans la réponse.
      loadedSiteIds: loadedSiteIds ?? sites,
      multiBrand: meta.multiBrand === true,
      failedSiteIds: Array.isArray(meta.failedSiteIds) ? meta.failedSiteIds.map(toNumber) : [],
    },
  };
}

function apiBaseUrl(): string {
  // = utils/http HTTP.getInput (méthode privée) : même base d'URL que toutes les requêtes de l'app.
  return (
    (typeof mondayMindbody !== 'undefined' && mondayMindbody?.apiBaseUrl) ||
    import.meta.env.VITE_API_BASE_URL ||
    `${window.location.origin}/wp-json/mindbody/v1`
  );
}

export class PlanningRouteMissing extends Error {}

/**
 * Options de `fetchPlanningRoute`. `credentials: 'omit'` : requête sans le cookie de session (la route est publique,
 * sa réponse est la même pour tous) ; le serveur ne l'aligne donc pas derrière les autres requêtes du navigateur, que le
 * verrou de la session PHP fait passer une par une : lecture faite PENDANT une réservation (suites de la confirmation,
 * SPEC CO4 et CO5).
 */
export interface PlanningFetchOptions {
  credentials?: RequestCredentials;
}

/**
 * `GET class/planning` seule, SANS repli ; lève PlanningRouteMissing sur un 404 (route absente, ou v2 inactive pour le
 * serveur : aperçu administrateur `?ux=v2` lu sans le cookie). Appelée telle quelle par les suites de la confirmation
 * (screens/Class/useFollowUps), qui ne se rabattent jamais sur la route v1.
 */
export async function fetchPlanningRoute(params: PlanningParams, options: PlanningFetchOptions = {}): Promise<PlanningResponse> {
  const response = await fetch(`${apiBaseUrl()}/class/planning?${planningQueryString(params)}`, {
    method: 'GET',
    credentials: options.credentials ?? 'include',
  });
  if (response.status === 404) throw new PlanningRouteMissing('class/planning: 404');
  if (!response.ok) throw new Error(`class/planning: HTTP ${response.status}`);
  return normalizePlanningResponse(await response.json(), params.currentSiteId);
}

/** Repli : route v1 `GET class` (site seul), mêmes paramètres que hooks/useApiQueries useListClasses. */
async function fetchV1Classes(params: PlanningParams): Promise<PlanningResponse> {
  const query: Record<string, string> = { week: String(params.week), year: String(params.year), studioId: 'null' };
  if (params.coachId) query.coachId = String(params.coachId);
  const classes = await HTTP.get<MindbodyClass[]>('/class', query).send();
  const siteId = toNumber(params.currentSiteId);
  return {
    classes: normalizeClasses(classes),
    meta: { siteIds: [siteId], loadedSiteIds: [siteId], multiBrand: false, failedSiteIds: [], fallback: true },
  };
}

export async function fetchPlanning(params: PlanningParams): Promise<PlanningResponse> {
  try {
    return await fetchPlanningRoute(params);
  } catch (error) {
    if (error instanceof PlanningRouteMissing) return fetchV1Classes(params);
    throw error;
  }
}

export function planningQueryKey({
  week,
  year,
  coachId,
  siteId,
}: Pick<PlanningParams, 'week' | 'year' | 'coachId' | 'siteId'>) {
  return ['v2', 'class/planning', week, year, coachId ?? null, siteId ?? null] as const;
}

/**
 * Planning d'une semaine d'une marque. Une requête par semaine et par marque affichées ; données gardées 10 min en
 * cache (le cache React Query est persisté en localStorage par main.tsx : on ne garde pas des semaines entières
 * pendant 24 h).
 */
export function usePlanning(params: PlanningParams) {
  return useQuery({
    queryKey: planningQueryKey(params),
    queryFn: () => fetchPlanning(params),
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
